import { useCallback } from "react";

import notification from "antd/es/notification";
import { defineMessages } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import SavedFilter, {
  RegularSavedFilter,
} from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import i18nService from "@app/config/I18nService";
import convertFilterModelToMapFilterModel from "@app/store/savedFilters/convertFilterModelToMapFilterModel";
import convertMapFilterModelToFilterModel from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import { isSimpleCondition } from "@app/util/viewModel/assert";

const messages = defineMessages({
  funnelDoesNotExist: {
    id: "deal.kanbanView.savedFilter.error.funnelDoesNotExist",
    defaultMessage:
      "This saved filter has a funnel which does not exist anymore. Please select a single funnel for this filter or drill down by funnel using the dropdown below.",
    description:
      "An error message when user selects Saved Filter with a funnel in filter which no longer exists on the Kanban view",
  },
  tooManyFunnels: {
    id: "deal.kanbanView.savedFilter.error.tooManyFunnels",
    defaultMessage:
      "This saved filter has multiple funnels selected and is not supported by this view. Please select a single funnel for this filter or drill down by funnel using the dropdown below.",
    description:
      "An error message when user selects Saved Filter with multiple funnels in filter on the Kanban view",
  },
});

const updateSavedFilterToFilterByGivenFunnel = (
  savedFilter: RegularSavedFilter,
  funnelId: Funnel["id"]
): RegularSavedFilter => ({
  ...savedFilter,
  filters: {
    ...savedFilter.filters,
    funnel: { operator: FilterOperator.EQUALS, value: funnelId },
  },
});

// Kanban page can only accept one Funnel in filters
// So we're checking if this condition is met and are updating the filter to satisfy condition.
const useSavedFilterProcessor = (funnels: Funnel[], funnelId: Funnel["id"] | undefined) =>
  useCallback(
    (rawSavedFilter: SavedFilter): SavedFilter | undefined => {
      const savedFilter = {
        ...rawSavedFilter,
        filters: convertMapFilterModelToFilterModel(EntityType.DEAL, rawSavedFilter.filters),
      } as RegularSavedFilter;

      // First we check if there's any funnel filter at all
      if (
        !savedFilter.filters.funnel ||
        !isSimpleCondition(savedFilter.filters.funnel) ||
        ![FilterOperator.EQUALS, FilterOperator.IN_ANY].includes(
          savedFilter.filters.funnel.operator
        )
      ) {
        // If not, we use current funnel to filter by (or if it's not defined, the first one)
        const resultingSavedFilter = updateSavedFilterToFilterByGivenFunnel(
          savedFilter,
          funnelId ?? funnels[0].id
        );
        return {
          ...resultingSavedFilter,
          filters: convertFilterModelToMapFilterModel(
            EntityType.DEAL,
            resultingSavedFilter.filters
          ),
        };
      }

      // If yes, then we ensure that this filter only has one funnel in it, and the operator is EQUALS
      const { operator, value } = savedFilter.filters.funnel;

      if (operator === FilterOperator.EQUALS) {
        // We check if such funnel still exists
        if (funnels.some(({ id }) => id === value)) {
          return rawSavedFilter;
        }
        // if not, then we take current funnel or the first one
        notification.warn({
          key: "funnelDoesNotExist",
          message: i18nService.formatMessage(messages.funnelDoesNotExist),
        });
        const resultingSavedFilter = updateSavedFilterToFilterByGivenFunnel(
          savedFilter,
          funnelId ?? funnels[0].id
        );
        return {
          ...resultingSavedFilter,
          filters: convertFilterModelToMapFilterModel(
            EntityType.DEAL,
            resultingSavedFilter.filters
          ),
        };
      }

      // If it was IN_ANY condition but there was just one funnel selected, we can work with this
      if (value.length === 1) {
        // Let's just replace it with an EQUALS operator
        const resultingSavedFilter = updateSavedFilterToFilterByGivenFunnel(savedFilter, value[0]);
        return {
          ...resultingSavedFilter,
          filters: convertFilterModelToMapFilterModel(
            EntityType.DEAL,
            resultingSavedFilter.filters
          ),
        };
      }

      // Otherwise, saved filter has invalid funnel filter (for the Kanban page) and
      // it needs to be replaced
      notification.warn({
        key: "tooManyFunnels",
        message: i18nService.formatMessage(messages.tooManyFunnels),
      });
      const resultingSavedFilter = updateSavedFilterToFilterByGivenFunnel(
        savedFilter,
        funnelId ?? funnels[0].id
      );
      return {
        ...resultingSavedFilter,
        filters: convertFilterModelToMapFilterModel(EntityType.DEAL, resultingSavedFilter.filters),
      };
    },
    [funnelId, funnels]
  );

export default useSavedFilterProcessor;
